import type { FunctionComponent } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';

import type { TemplateFunction } from 'Components/types';
import { toggle } from 'Core/actions/request';
import { createFitmentSearchResponseAllFacetsSelector } from 'Core/selectors/fitmentSearch/index';
import {
  createFacetSelector,
  searchQuerySelector,
  searchRequestSelectionSelector,
  totalHitsSelector,
} from 'Core/selectors/search';
import { useDispatch, useSelector } from 'Core/store';
import type { SelectionEntry } from 'Models/index';
import { getFacetToggleState, setItem } from 'Utils/userStorage';
import keys from 'Utils/userStorageKeys';

export type Params = {
  isToggled: boolean | null;
  toggleFacet: () => void;
};

export type FacetToggleConfig = {
  toggledFacet?: SelectionEntry;
  fieldsForVisibility?: Array<string>;
  initToggled?: boolean;
  isInverted?: boolean;
  saveLastState?: boolean;
};

export type Props = FacetToggleConfig & {
  template: TemplateFunction<Params>;
};

const FacetToggle: FunctionComponent<Props> = ({
  template,
  toggledFacet,
  fieldsForVisibility,
  initToggled,
  isInverted,
  saveLastState,
}) => {
  const dispatch = useDispatch();

  const facetToggleField = keys.facetToggleField(toggledFacet?.field ?? '');
  const [isToggled, setIsToggled] = useState(() =>
    saveLastState ? getFacetToggleState(toggledFacet?.field ?? '') : null,
  );

  const isVisible = useVisible(fieldsForVisibility, toggledFacet);

  const isToggledOnServer = useSelector(searchRequestSelectionSelector).some(
    (el) => !!toggledFacet && el.field === toggledFacet.field && el.term === toggledFacet.term,
  );

  useEffect(
    function updateUniversalBadgeVisibility() {
      if (toggledFacet?.field === 'Universal') {
        window.document.body.classList.toggle('cm_hide-universal-badge', !isVisible);
      }
    },
    [isVisible, toggledFacet?.field],
  );

  useEffect(() => {
    if (!toggledFacet || !isVisible) {
      return;
    }

    if (toggledFacet.field === 'FilterByVehicle') {
      setIsToggled(isToggledOnServer);
      if (saveLastState) {
        setItem(facetToggleField, isToggledOnServer);
      }
      return;
    }

    if (isToggledOnServer && typeof isToggled !== 'boolean') {
      setIsToggled(true);

      if (saveLastState) {
        setItem(facetToggleField, true);
      }
      return;
    }

    if (
      !isToggledOnServer &&
      ((saveLastState && isToggled) || (typeof isToggled !== 'boolean' && initToggled))
    ) {
      dispatch(toggle(toggledFacet));
      setIsToggled(true);
      if (saveLastState) {
        setItem(facetToggleField, true);
      }
      return;
    }

    if (typeof isToggled === 'boolean' && isToggled !== isToggledOnServer) {
      setIsToggled(isToggledOnServer);
      if (saveLastState) {
        setItem(facetToggleField, isToggledOnServer);
      }
    }
  }, [
    dispatch,
    isVisible,
    initToggled,
    isToggled,
    isToggledOnServer,
    toggledFacet,
    saveLastState,
    facetToggleField,
  ]);

  if (!toggledFacet || !isVisible) {
    return null;
  }

  const toggleFacet = () => {
    if (saveLastState) {
      setItem(facetToggleField, !isToggled);
    }

    setIsToggled(!isToggled);
    dispatch(toggle(toggledFacet, { extra: { facetToggleJustToggled: toggledFacet.field } }));
  };

  return template.call({
    isToggled: isInverted ? !isToggled : isToggled,
    toggleFacet,
  });
};

export default FacetToggle;

function useVisible(
  fieldsForVisibility: Array<string> = [],
  toggledFacet: SelectionEntry | undefined,
): boolean {
  const facetToToggle = useSelector(createFacetSelector(toggledFacet?.field ?? ''));
  const responseTotalHits = useSelector(totalHitsSelector);
  const isToggleSelected = facetToToggle?.selection.some(
    (value) => value.term === (toggledFacet?.term ?? ''),
  );
  const isToggleMatchingAllHits =
    toggledFacet?.field !== 'FilterByVehicle' &&
    !isToggleSelected &&
    facetToToggle?.totalHits === responseTotalHits &&
    facetToToggle?.values.find((v) => v.term === (toggledFacet?.term ?? ''))?.hitCount === responseTotalHits;

  const responseQuery = useSelector(searchQuerySelector);
  const hideBySearchQuery = responseQuery && toggledFacet?.field === 'Universal';

  const facetsSelector = useMemo(() => createFitmentSearchResponseAllFacetsSelector(), []);
  const isAllFacetsHaveSelection = useSelector((state) => {
    const facets = facetsSelector(state);
    return fieldsForVisibility.every((field) => facets.get(field)?.selection.length);
  });

  return facetToToggle != null && !isToggleMatchingAllHits && !hideBySearchQuery && isAllFacetsHaveSelection;
}
